
import { useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";
import type { Signature } from "@/types/supabase";
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';

type SignatureChanges = RealtimePostgresChangesPayload<{
  [key: string]: any;
}>;

const POLLING_INTERVAL = 5000;
const FAST_POLLING_COUNT = 3;
const FAST_POLLING_INTERVAL = 1000;

export const useSignatureSubscription = (
  user: User | null,
  setSignatures: React.Dispatch<React.SetStateAction<Signature[]>>
) => {
  const fastPollingCountRef = useRef(0);
  const lastChangeTimeRef = useRef(0);
  const mountedRef = useRef(true);

  // Добавляем функцию для безопасного обновления состояния
  const safeSetSignatures = useCallback((updater: React.SetStateAction<Signature[]>) => {
    if (mountedRef.current) {
      setSignatures(updater);
    }
  }, [setSignatures]);

  const fetchSignatures = useCallback(async () => {
    if (!user || !mountedRef.current) return;

    try {
      console.log('[useSignatureSubscription] Fetching signatures');
      const { data, error } = await supabase
        .from('signatures')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[useSignatureSubscription] Error fetching signatures:', error);
        return;
      }

      if (data && mountedRef.current) {
        console.log('[useSignatureSubscription] Signatures loaded:', data.length);
        safeSetSignatures(data);
      }
    } catch (err) {
      console.error('[useSignatureSubscription] Error:', err);
    }
  }, [user, safeSetSignatures]);

  const startFastPolling = useCallback(() => {
    fastPollingCountRef.current = FAST_POLLING_COUNT;
    lastChangeTimeRef.current = Date.now();
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    
    if (!user) return;

    console.log('[useSignatureSubscription] Setting up subscription for user:', user.id);
    
    const channel = supabase
      .channel('schema-db-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'signatures',
          filter: `user_id=eq.${user.id}`,
        },
        (payload: SignatureChanges) => {
          console.log('[useSignatureSubscription] Received change:', payload.eventType, payload);
          startFastPolling();
          
          if (!mountedRef.current) return;
          
          safeSetSignatures(currentSignatures => {
            if (!payload.new && !payload.old) return currentSignatures;

            switch (payload.eventType) {
              case 'INSERT':
                const newSignature = payload.new as Signature;
                if (currentSignatures.some(sig => sig.id === newSignature.id)) {
                  return currentSignatures;
                }
                return [newSignature, ...currentSignatures];
              
              case 'DELETE':
                return currentSignatures.filter(sig => sig.id !== payload.old.id);
              
              case 'UPDATE':
                return currentSignatures.map(sig => 
                  sig.id === payload.new.id ? (payload.new as Signature) : sig
                );
              
              default:
                return currentSignatures;
            }
          });
        }
      )
      .subscribe(status => {
        console.log('[useSignatureSubscription] Subscription status:', status);
      });

    fetchSignatures();

    return () => {
      console.log('[useSignatureSubscription] Cleaning up subscription');
      mountedRef.current = false;
      supabase.removeChannel(channel);
    };
  }, [user?.id, fetchSignatures, startFastPolling, safeSetSignatures]);

  useEffect(() => {
    if (!user) return;
    
    const pollData = async () => {
      const now = Date.now();
      const timeSinceLastChange = now - lastChangeTimeRef.current;
      
      if (fastPollingCountRef.current > 0) {
        await fetchSignatures();
        fastPollingCountRef.current--;
        
        if (fastPollingCountRef.current > 0 && mountedRef.current) {
          setTimeout(pollData, FAST_POLLING_INTERVAL);
          return;
        }
      }
      
      if (timeSinceLastChange > POLLING_INTERVAL) {
        await fetchSignatures();
      }
    };

    const intervalId = setInterval(pollData, POLLING_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [user, fetchSignatures]);
};
